import React, { useRef, useId, useState } from 'react';
// Only setupFeeConfig now. The summary used to import the whole catalog to rebuild its own
// line items; it renders cart.lineItems instead, so DynamicForm no longer touches products
// directly at all -- the visible* lists arrive from useCheckoutForm already filtered.
import { setupFeeConfig } from './product-config.mjs';
import { FEATURES } from './lib/pricing/index.js';
import ShareConfigButton from './components/ShareConfigButton';
import SectionCard from './checkout/ui/SectionCard';
import SelectableCard from './checkout/ui/SelectableCard';
import RadioCardGroup from './checkout/ui/RadioCardGroup';
import SubmitBar from './checkout/sections/SubmitBar';
import OrderSummary from './checkout/sections/OrderSummary';
import QuestionCard from './checkout/sections/QuestionCard';
import AddonSubscriptionsSection from './checkout/sections/AddonSubscriptionsSection';
import OneTimeServicesSection from './checkout/sections/OneTimeServicesSection';
import CheckoutHero from './checkout/sections/CheckoutHero';
import ResumeBanner from './checkout/sections/ResumeBanner';
import StaleLinkBanner from './checkout/sections/StaleLinkBanner';
import TermsSection from './checkout/sections/TermsSection';
import CouponField from './checkout/sections/CouponField';
import MailchimpOptIn from './checkout/sections/MailchimpOptIn';
import CustomerDetailsSection from './checkout/sections/CustomerDetailsSection';
import useCheckoutForm, { applyCoupon } from './checkout/state/useCheckoutForm';
import { REQUIRED_CUSTOMER_FIELDS } from './checkout/state/checkoutSchema';
import { partitionQuestions, answeredCount } from './checkout/content/questionGrouping';

// Legal links shown next to the terms checkbox.
//
// CRA inlines process.env.REACT_APP_* at BUILD time (webpack DefinePlugin), so these must
// be set as Cloudflare Pages *build* environment variables -- setting them as Worker
// runtime bindings silently does nothing. The defaults are therefore absolute URLs to the
// real policy pages: a misconfigured build must still render working legal links. These
// previously defaulted to '/terms' and '/privacy', which are not routes, so both links 404'd.
const TERMS_URL = process.env.REACT_APP_TERMS_URL || 'https://decisivesites.com/terms-of-use/';
const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || 'https://decisivesites.com/privacy-policy/';

// NOTE: SAVED_STATE_MAX_AGE_MS moved to checkout/state/urlPrefill.js, alongside the read and
// write functions it governs -- the TTL and the code that enforces it now live together.

// NOTE: The local Tooltip component was removed with its only usage. It was mouse-only
// (onMouseEnter/onMouseLeave on a non-focusable div), so it had no keyboard or touch
// access, no role="tooltip", and no aria-describedby. If tooltips return, build an
// accessible InfoTooltip on a real <button> with focus/blur and Escape handling.

export default function DynamicForm() {
  // The <form> element, so the mount-time autofill check can read every named input at once
  // via FormData instead of six getElementById lookups.
  const formRef = useRef(null);

  /*
    Owned here because it spans two components. OrderSummary renders the blocker panel with this
    id and SubmitBar points aria-describedby at it, so a screen reader user hears why the form
    will reject when focus reaches the button. Generated with useId rather than a constant so
    two forms on one page could not collide.
  */
  const blockerListId = useId();

  /*
    Has the customer tried to submit yet?

    STANDARD VALIDATION TIMING: the "Still needed" panel stays hidden until they attempt to
    submit. Showing it on load told someone who had typed nothing that five things were wrong,
    which is noise rather than help -- they know the form is empty.

    Local state rather than the reducer for two reasons. It is not derivable: SUBMIT_BLOCKED and
    FIELD_BLURRED both write to ui.errors, so a non-empty errors object cannot distinguish "they
    pressed submit" from "they tabbed out of one field". And checkoutReducer.js is off limits for
    this refactor. It records an event that happened, not a fact about the cart, so it does not
    reintroduce the derived-state problem the reducer rewrite removed.

    Never reset. Once someone has submitted, keeping the outstanding list visible as they fix
    each item is the point.
  */
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Registry of field inputs by name, populated by CustomerDetailsSection. Lets the submit
  // blocker list focus a field without reaching into the document.
  const fieldRefs = useRef({});

  /**
   * ALL form state now lives in one reducer behind this hook.
   *
   * What this replaced: 20 useState calls and four useEffects. Three of those effects existed
   * only to express "when X changes, Y must also change" -- a relationship a reducer states
   * directly and atomically:
   *
   *   - visibility reconciliation needed isLoadingFromUrlRef plus a 500ms timer to tell a
   *     user's click apart from a URL hydration, because as an effect it could not see WHY the
   *     plan changed. PLAN_SELECTED and HYDRATED each know, so both the ref and the timer are
   *     gone -- not tidied, unnecessary.
   *   - the URL/localStorage restore called ~13 setState functions through a switch statement,
   *     then raced reconciliation. It is now one HYDRATED dispatch.
   *   - error clearing listed `errors` in its own dependency array while calling setErrors.
   *
   * cart, the visible* lists, and blockers are DERIVED here, never stored.
   */
  const {
    customer,
    selection,
    coupon,
    consent,
    ui,
    cart,
    questions,
    visiblePrimary,
    visibleAddons,
    visibleOnetime,
    blockers,
    shareUrl,
    // canSubmit is intentionally not read here: SubmitBar derives its own state from
    // `blockers` and never disables the button except while submitting. The hook still
    // exposes it for callers that want a single boolean.
    handlers,
    validateAll,
    submitLifecycle,
    dispatch,
  } = useCheckoutForm({ formRef });

  /**
   * Scroll a field into view and focus it.
   *
   * `preventScroll: true` is what removes the 300ms setTimeout this code used to need.
   * Calling focus() normally cancels an in-flight smooth scroll, so the old code guessed at
   * the animation duration and focused afterwards. preventScroll focuses without scrolling,
   * so the smooth scroll finishes undisturbed -- correct regardless of animation timing.
   */
  const scrollToAndFocus = (fieldId) => {
    // Prefer the ref registry; fall back to a DOM lookup for controls outside
    // CustomerDetailsSection (the terms checkbox).
    const element = fieldRefs.current[fieldId] || document.getElementById(fieldId);
    if (!element) return false;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus({ preventScroll: true });
    return true;
  };

  /**
   * Focus the first problem field, in page order.
   *
   * Takes the errors validateAll just returned rather than reading `blockers`. `blockers` is
   * computed during render, so inside the submit handler it still reflects the render BEFORE
   * the SUBMIT_BLOCKED dispatch -- focusing from it would be one submit behind. Passing the
   * fresh errors in makes the ordering explicit instead of dependent on React's batching.
   */
  const focusFirstError = (errors) => {
    const order = [...REQUIRED_CUSTOMER_FIELDS, 'agreedToTerms'];
    for (const field of order) {
      if (errors[field] && scrollToAndFocus(field)) return;
    }
  };

  // Price display for a question answer, which varies by primary plan via conditionalPricing.
  const getAnswerPriceDisplay = (answer, primaryPlanId) => {
    if (!answer?.addProduct || !primaryPlanId) return null;

    const { conditionalPricing, fixedProduct } = answer.addProduct;

    if (conditionalPricing && conditionalPricing[primaryPlanId]) {
      return conditionalPricing[primaryPlanId].price_display;
    }
    return fixedProduct ? fixedProduct.price_display : null;
  };

  /*
    DERIVED, never stored -- same rule as cart and blockers. The split is recomputed from the
    catalog on every render, so a config change cannot leave a question filed under the wrong
    heading.
  */
  const { priced: pricedQuestions, interest: interestQuestions } = partitionQuestions(questions);

  // One renderer for both sections: they differ in heading and ground, not in row behavior.
  const renderQuestion = (question) => (
    <QuestionCard
      key={question.id}
      question={question}
      value={selection.questionAnswers[question.id]}
      term={selection.term}
      getPrice={(answer) => getAnswerPriceDisplay(answer, selection.primaryPlanId)}
      onAnswer={question.type === 'radio' ? handlers.answerRadio : handlers.answerCheckbox}
    />
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Before validating, so the panel appears on the first rejected attempt rather than the
    // second.
    setSubmitAttempted(true);

    // Validation lives in the hook so the same validateCustomer runs here and on the server.
    // A failure dispatches SUBMIT_BLOCKED, which populates ui.errors in one transition, and
    // returns those errors so focus can move without waiting for the re-render.
    const errors = validateAll();
    if (errors) {
      focusFirstError(errors);
      return;
    }

    submitLifecycle.started();

    // Send the SELECTION, not lookup keys or prices.
    //
    // The server runs resolveCart() on this itself, so the keys and amounts charged are its
    // own output rather than client input. That makes a crafted basket unrepresentable --
    // there is no field in which to express one -- and gets the visibility rules enforced
    // server-side for free.
    const payload = {
      selection,
      ...customer,
      mailchimpOptIn: consent.mailchimpOptIn,
      coupon_code: coupon.applied ? coupon.code : undefined,
      coupon_type: coupon.applied ? coupon.applied.type : undefined,
    };

    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let message = 'Failed to create checkout session';
        let fieldErrors;
        try {
          const errorData = await res.json();
          if (errorData?.error || errorData?.message) {
            message = errorData.error || errorData.message;
          }
          // Server-side field errors land on their own fields rather than in an alert().
          if (errorData?.errors) fieldErrors = errorData.errors;
        } catch {
          // A non-JSON error body carries nothing we can show the customer.
        }
        submitLifecycle.failed(message, fieldErrors);
        return;
      }

      const data = await res.json();
      if (data?.url) {
        // The ONE place customer PII is persisted: the customer is leaving for Stripe and may
        // cancel and come back. Previously every keystroke wrote name/email/phone to
        // localStorage, so someone who typed their details and abandoned without ever
        // submitting still left them on the device.
        submitLifecycle.persistForResume();
        window.location.href = data.url;
        return;
      }

      // A 200 with no url used to do nothing at all: the button simply stopped spinning and
      // the customer was left on a form that appeared to have worked.
      submitLifecycle.failed('Checkout session was created without a redirect URL. Please try again.');
    } catch (err) {
      console.error('Checkout session request failed:', err);
      submitLifecycle.failed('Failed to create checkout session. Please check your connection and try again.');
    } finally {
      submitLifecycle.settled();
    }
  };

  return (
    <div className="mx-auto min-w-80 max-w-shell border-x border-gray-200 bg-white">
      <CheckoutHero />

      {ui.showResumeMessage && <ResumeBanner onDismiss={handlers.dismissResume} />}

      {/* A shared link that named retired or now-incompatible items. */}
      <StaleLinkBanner items={ui.unresolvedParams} onDismiss={handlers.dismissStaleLink} />

      {/*
        FORM LEFT, SUMMARY RIGHT at `lg` and up; one column below it.

        `items-start` is what makes the sticky summary work: a grid item stretches to the row
        height by default, so the column would already be as tall as the form and would have
        nothing left to stick within. Aligning it to the start lets it be short enough to move.

        pb-28 below `sm` reserves room for the fixed submit bar, which is pinned only on phones.
        Without it the bar covers the last thing in the form -- which, in this order, is the
        terms checkbox.
      */}
      <form
        ref={formRef}
        className="grid grid-cols-1 items-start pb-28 sm:pb-0 lg:grid-cols-[1fr_340px]"
        onSubmit={handleSubmit}
      >
      <div className="min-w-0 lg:border-r lg:border-gray-200">
      <CustomerDetailsSection
        values={customer}
        errors={ui.errors}
        onChange={handlers.onFieldChange}
        onBlur={handlers.onFieldBlur}
        fieldRefs={fieldRefs}
      />

      {/* Primary Subscription Plan Section */}
      {/*
        The info tooltip that used to sit beside this heading was removed: it shipped Lorem
        ipsum placeholder text to production, visible on hover, and was mouse-only. If real
        explanatory copy is ever needed, a plan comparison belongs in the cards themselves,
        not in a hover-only tooltip.
      */}
      {/*
        SECTION 2 IS ONE SECTION, not the two it used to be.

        Plan and billing period were separate cards, which meant the price shown on a plan card
        depended on a control the customer had not scrolled to yet. The redesign puts them in
        one numbered step so the term toggle sits directly under the prices it changes.

        Two <fieldset>s remain inside it, each with its own sr-only legend, so the grouping a
        screen reader hears is still per-question rather than one undifferentiated blob.

        No "Compare plans" link, though the mockup draws one. It has no destination, and the
        comment above records that this repo already removed a plan-comparison affordance for
        shipping placeholder content. A dead link is worse than an absent one.
      */}
      <SectionCard title="Plan &amp; billing" stepNumber={2}>
        <RadioCardGroup legend="Primary subscription plan">
          {visiblePrimary.map((p) => {
            const termObj = p.terms.find((t) => t.id === selection.term);
            return (
              <SelectableCard
                key={p.id}
                type="radio"
                id={`plan-${p.id}`}
                name="primaryPlan"
                value={p.id}
                checked={selection.primaryPlanId === p.id}
                onChange={() => handlers.selectPlan(p.id)}
                title={p.name}
                subtitle={selection.term === 'monthly' ? 'Monthly' : 'Yearly'}
                // No `|| 0` fallback. The previous markup rendered "$0" when a term did not
                // resolve, so a config error looked like a free plan; showing no price makes
                // it look broken instead, which is the honest failure.
                priceLabel={termObj ? `$${termObj.price_display}` : undefined}
                priceSuffix={selection.term === 'monthly' ? '/mo' : '/yr'}
              />
            );
          })}
        </RadioCardGroup>
        <RadioCardGroup legend="Billing period">
          <SelectableCard
            type="radio"
            id="term-monthly"
            name="termlength"
            value="monthly"
            checked={selection.term === 'monthly'}
            onChange={() => handlers.selectTerm('monthly')}
            title="Monthly"
            // Derived, not hardcoded: this used to read "Includes $99 setup fee" as a
            // literal, so changing the fee (or disabling it) would have left the page
            // promising a price checkout did not charge.
            subtitle={
              selection.primaryPlanId && setupFeeConfig.enabled
                ? `Includes $${setupFeeConfig.monthlySetupFee.price_display.toFixed(0)} setup fee`
                : undefined
            }
          />
          <SelectableCard
            type="radio"
            id="term-yearly"
            name="termlength"
            value="yearly"
            checked={selection.term === 'yearly'}
            onChange={() => handlers.selectTerm('yearly')}
            title="Yearly"
            subtitle={selection.primaryPlanId ? 'No setup fee' : undefined}
          />
        </RadioCardGroup>
      </SectionCard>

      {/*
        SECTIONS 3 AND 4 ARE ONE LIST, SPLIT BY PRICE.

        These were a single "Additional Services & Options" card that mixed two very different
        propositions: choices that change what the customer pays today, and expressions of
        interest that charge nothing and only start a conversation. Presenting them identically
        made the second group look like more upsell, which is the opposite of the intent.

        The partition is derived from the catalog, not declared. See questionGrouping.js for the
        consequence of attaching a price to an answer in section 4.
      */}
      {selection.primaryPlanId && pricedQuestions.length > 0 && (
        <SectionCard
          title="Add to your package"
          stepNumber={3}
          description="Priced options. Change any of these before you pay."
          status={
            <span className="text-sm tabular-nums text-gray-500">
              {answeredCount(pricedQuestions, selection.questionAnswers)} of{' '}
              {pricedQuestions.length} answered
            </span>
          }
        >
          <div className="space-y-3.5">{pricedQuestions.map(renderQuestion)}</div>
        </SectionCard>
      )}

      {selection.primaryPlanId && interestQuestions.length > 0 && (
        <SectionCard
          title="Interested in growing further?"
          stepNumber={4}
          tinted
          /*
            The description no longer opens with "Nothing here is charged today." The tag beside
            the heading says it, so the paragraph does not need to -- the section previously
            stated the same fact three times over: here, in the tag, and again in every answer
            row that shows no price.
          */
          description="Say you are curious and we will follow up with a plan and a price."
          tag={
            // No decorative dot: it added a third element to a two-word label and read as a
            // status indicator for something that has no states.
            <span className="border border-primary-200 bg-white px-2 py-1 text-chip font-semibold uppercase text-primary-700">
              No cost to ask
            </span>
          }
          status={
            <span className="text-sm tabular-nums text-gray-500">
              {answeredCount(interestQuestions, selection.questionAnswers)} of{' '}
              {interestQuestions.length} answered
            </span>
          }
        >
          <div className="space-y-3.5">{interestQuestions.map(renderQuestion)}</div>
        </SectionCard>
      )}

      <AddonSubscriptionsSection
        enabled={FEATURES.addonSubscriptions}
        addons={visibleAddons}
        selected={selection.addonIds}
        term={selection.term}
        onToggle={handlers.toggleAddon}
      />

      <OneTimeServicesSection
        enabled={FEATURES.oneTimeServices}
        products={visibleOnetime}
        selected={selection.oneTimeIds}
        onToggle={handlers.toggleOneTime}
      />

      {/* Validation message */}
      {!selection.primaryPlanId && (
        <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          Please select a primary subscription plan.
        </div>
      )}

      </div>

      {/*
        THE SUMMARY IS NOW ONE COMPONENT RENDERING cart.lineItems.

        What was here: ~180 lines of <table> that re-walked primaryProducts,
        addonSubscriptions, oneTimeProducts and questions to rebuild the line items, including
        its own copy of the conditional-pricing lookup and the radio-versus-checkbox branch.
        resolveCart already produces exactly that list, labelled and priced, in the same
        traversal that produces the lookup keys sent to Stripe. The table was a sixth copy, and
        it could show a row the cart did not charge.

        Terms, the opt-in and the submit control follow the summary rather than preceding it,
        matching the redesign: the customer sees what they owe, then agrees, then continues.
      */}
      <div className="space-y-4 px-4 py-5 sm:px-7 sm:py-6 lg:sticky lg:top-0 lg:max-h-screen lg:overflow-y-auto">
        <OrderSummary
          cart={cart}
          term={selection.term}
          appliedCoupon={coupon.applied}
          // Empty until they have tried to submit: see submitAttempted above.
          blockers={submitAttempted ? blockers : []}
          blockerListId={blockerListId}
          onFocusField={scrollToAndFocus}
          couponSlot={
            <CouponField
              code={coupon.code}
              applied={coupon.applied}
              error={coupon.error}
              validating={coupon.validating}
              expanded={coupon.expanded}
              onCodeChange={handlers.setCouponCode}
              // applyCoupon is the one async action, so it lives beside the hook rather than in
              // the reducer, which must stay pure.
              onApply={() => applyCoupon(dispatch, coupon.code)}
              onRemove={handlers.removeCoupon}
              onExpand={handlers.expandCoupon}
              onCancel={handlers.cancelCoupon}
            />
          }
        />

        <TermsSection
          checked={consent.agreedToTerms}
          error={ui.errors.agreedToTerms}
          // One dispatch: TERMS_TOGGLED clears its own error when the box is checked, so the
          // caller no longer has to remember to do that separately.
          onChange={handlers.setAgreedToTerms}
          termsUrl={TERMS_URL}
          privacyUrl={PRIVACY_URL}
        />

        <MailchimpOptIn checked={consent.mailchimpOptIn} onChange={handlers.setMailchimpOptIn} />

        <SubmitBar
          // Only the COUNT, not the list. The list itself renders in OrderSummary; passing it
          // here too would invite a second copy of the panel drifting into existence.
          blockerCount={submitAttempted ? blockers.length : 0}
          blockerListId={blockerListId}
          submitting={ui.submitting}
          submitError={ui.submitError}
          dueToday={cart.totals.total}
        />

      </div>
    </form>

    {/*
      One prop, not a 12-field object the component had to reassemble into a URL. The builder
      now lives beside the parser in urlPrefill.js, so the two are provably symmetric -- which
      is what makes the round-trip test possible, and that test is what would have caught
      questionAnswers being dropped from every shared link.
    */}
    <ShareConfigButton shareUrl={shareUrl} />
    </div>
  );
}
